import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProviderAccessGuardProps {
  children: React.ReactNode;
  allowUnderReview?: boolean; // Allow under-review providers (for profile page)
}

/**
 * ProviderAccessGuard - Higher-order component to protect provider routes
 * 
 * This component ensures that:
 * 1. Only providers can access provider routes
 * 2. Under-review providers are redirected unless explicitly allowed
 * 3. Continuous monitoring prevents manual navigation bypass
 */
const ProviderAccessGuard: React.FC<ProviderAccessGuardProps> = ({ 
  children, 
  allowUnderReview = false 
}) => {
  const { user, isProvider, isProviderUnderReview } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If user is not authenticated or not a provider, redirect to home
    if (!user || !isProvider()) {
      navigate('/', { replace: true });
      return;
    }

    // If provider is under review and this route doesn't allow it, redirect
    if (isProviderUnderReview() && !allowUnderReview) {
      navigate('/provider/under-review', { replace: true });
      return;
    }
  }, [user, isProvider, isProviderUnderReview, allowUnderReview, navigate]);

  // Monitor for status changes (less frequent to reduce performance impact)
  useEffect(() => {
    const interval = setInterval(() => {
      if (user && isProvider()) {
        // Check if provider status changed to under-review
        if (isProviderUnderReview() && !allowUnderReview) {
          navigate('/provider/under-review', { replace: true });
        }
      } else if (user) {
        // User role changed - no longer a provider
        navigate('/', { replace: true });
      }
    }, 5000); // Check every 5 seconds instead of every second

    return () => clearInterval(interval);
  }, [user, isProvider, isProviderUnderReview, allowUnderReview, navigate]);

  // Don't render children if access should be blocked
  if (!user || !isProvider()) {
    return null;
  }

  if (isProviderUnderReview() && !allowUnderReview) {
    return null;
  }

  return <>{children}</>;
};

export default ProviderAccessGuard;